export const WEFAT_ACTION_SHEET_API_NAMES = Object.freeze(['showActionSheet']);
export class WxActionSheetRuntimeError extends Error {
    api = 'showActionSheet';
    code;
    errCode;
    errMsg;
    remediation;
    constructor(code, reason, remediation, cause) {
        const errMsg = `showActionSheet:fail ${reason}`;
        super(`${code} ${errMsg}`);
        this.name = 'WxActionSheetRuntimeError';
        this.code = code;
        this.errCode = code;
        this.errMsg = errMsg;
        this.remediation = remediation;
        if (cause !== undefined)
            Object.defineProperty(this, 'cause', { value: cause });
    }
}
const CALLBACK_KEYS = new Set(['success', 'fail', 'complete']);
const OPTION_KEYS = new Set([...CALLBACK_KEYS, 'alertText', 'itemColor', 'itemList']);
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function optionError(reason) {
    return new WxActionSheetRuntimeError('WF6201', reason, 'Pass a non-empty string itemList with optional alertText, a six-digit itemColor, and callable callbacks.');
}
function environmentError(reason, cause) {
    if (cause instanceof WxActionSheetRuntimeError)
        return cause;
    return new WxActionSheetRuntimeError('WF6200', reason, 'Run the generated application in a browser with HTMLDialogElement support after document.body is available.', cause);
}
function cancellation() {
    return new WxActionSheetRuntimeError('WF6202', 'cancel', 'Treat action-sheet cancellation as a normal user choice and handle the fail callback when needed.');
}
function readPresentation(raw) {
    if (!isPlainRecord(raw))
        throw optionError('options must be a plain object');
    for (const key of Reflect.ownKeys(raw)) {
        const descriptor = typeof key === 'string' ? Object.getOwnPropertyDescriptor(raw, key) : undefined;
        if (typeof key !== 'string' || !OPTION_KEYS.has(key)) {
            throw optionError(`unsupported option field ${String(key)}`);
        }
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(`option field ${key} must be a direct data property`);
        }
        if (CALLBACK_KEYS.has(key) && descriptor.value !== undefined && typeof descriptor.value !== 'function') {
            throw optionError(`${key} must be a function when provided`);
        }
    }
    if (!Array.isArray(raw.itemList) || raw.itemList.length === 0 || raw.itemList.length > 100) {
        throw optionError('itemList must contain from 1 through 100 items');
    }
    const items = [];
    for (let index = 0; index < raw.itemList.length; index += 1) {
        const descriptor = Object.getOwnPropertyDescriptor(raw.itemList, String(index));
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(`itemList[${index}] must be a direct data property`);
        }
        const item = descriptor.value;
        if (typeof item !== 'string' || item.length === 0 || [...item].length > 1_024) {
            throw optionError(`itemList[${index}] must be a non-empty string of at most 1024 characters`);
        }
        items.push(item);
    }
    const alertText = raw.alertText ?? '';
    if (typeof alertText !== 'string' || [...alertText].length > 1_024) {
        throw optionError('alertText must be a string of at most 1024 characters when provided');
    }
    const itemColor = raw.itemColor ?? '#000000';
    if (typeof itemColor !== 'string' || !HEX_COLOR.test(itemColor)) {
        throw optionError('itemColor must be a six-digit hex color');
    }
    return {
        callbacks: raw,
        presentation: Object.freeze({
            alertText,
            itemColor: itemColor.toLowerCase(),
            itemList: Object.freeze(items),
        }),
    };
}
function invokeCallback(callback, value, kind, report) {
    if (callback === undefined)
        return;
    try {
        callback(value);
    }
    catch (error) {
        if (report !== undefined) {
            try {
                report(error, 'showActionSheet', kind);
                return;
            }
            catch (reportError) {
                queueMicrotask(() => { throw reportError; });
                return;
            }
        }
        queueMicrotask(() => { throw error; });
    }
}
function browserShowActionSheet(presentation) {
    if (typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') {
        return Promise.reject(environmentError('HTML dialog support is unavailable'));
    }
    const host = document.querySelector('[data-wefat-app]') ?? document.body;
    if (host === null)
        return Promise.reject(environmentError('document.body is unavailable'));
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = document.createElement('dialog');
    dialog.className = 'wefat-action-sheet';
    dialog.setAttribute('data-wefat-feedback', 'showActionSheet');
    dialog.setAttribute('aria-label', presentation.alertText || 'Actions');
    if (presentation.alertText.length > 0) {
        const alert = document.createElement('p');
        alert.className = 'wefat-action-sheet__alert';
        alert.textContent = presentation.alertText;
        dialog.appendChild(alert);
    }
    const list = document.createElement('div');
    list.className = 'wefat-action-sheet__list';
    const buttons = presentation.itemList.map((item, index) => {
        const button = document.createElement('button');
        button.autofocus = index === 0;
        button.className = 'wefat-action-sheet__item';
        button.type = 'button';
        button.textContent = item;
        button.style.color = presentation.itemColor;
        list.appendChild(button);
        return button;
    });
    const cancel = document.createElement('button');
    cancel.className = 'wefat-action-sheet__cancel';
    cancel.type = 'button';
    cancel.textContent = '\u53d6\u6d88';
    dialog.append(list, cancel);
    return new Promise((resolve, reject) => {
        let settled = false;
        const cleanup = () => {
            dialog.close();
            dialog.remove();
            previousFocus?.focus();
        };
        const select = (index) => {
            if (settled)
                return;
            settled = true;
            cleanup();
            resolve(index);
        };
        const dismiss = () => {
            if (settled)
                return;
            settled = true;
            cleanup();
            reject(cancellation());
        };
        buttons.forEach((button, index) => button.addEventListener('click', () => select(index)));
        cancel.addEventListener('click', dismiss);
        dialog.addEventListener('cancel', (event) => {
            event.preventDefault();
            dismiss();
        });
        dialog.addEventListener('click', (event) => {
            if (event.target === dialog)
                dismiss();
        });
        try {
            host.appendChild(dialog);
            dialog.showModal();
        }
        catch (error) {
            settled = true;
            dialog.remove();
            previousFocus?.focus();
            reject(environmentError('could not display the browser action sheet', error));
        }
    });
}
function browserEnvironment() {
    if (typeof document === 'undefined')
        return null;
    return { showActionSheet: browserShowActionSheet };
}
export function createBrowserActionSheetWxApis(options = {}) {
    if (options.onCallbackError !== undefined && typeof options.onCallbackError !== 'function') {
        throw new TypeError('onCallbackError must be a function when provided');
    }
    const environment = options.environment ?? browserEnvironment();
    if (environment === null || !isPlainRecord(environment) || typeof environment.showActionSheet !== 'function') {
        throw new TypeError('createBrowserActionSheetWxApis requires a browser document or injected environment');
    }
    return Object.freeze({
        showActionSheet(raw) {
            const { callbacks, presentation } = readPresentation(raw);
            const task = Promise.resolve()
                .then(() => environment.showActionSheet(presentation))
                .then((tapIndex) => Object.freeze({ errMsg: 'showActionSheet:ok', tapIndex }))
                .catch((error) => {
                throw environmentError(error instanceof Error ? error.message : 'browser action sheet failed', error);
            });
            if (callbacks.success === undefined && callbacks.fail === undefined && callbacks.complete === undefined) {
                return task;
            }
            void task.then((result) => {
                invokeCallback(callbacks.success, result, 'success', options.onCallbackError);
                invokeCallback(callbacks.complete, result, 'complete', options.onCallbackError);
            }, (error) => {
                invokeCallback(callbacks.fail, error, 'fail', options.onCallbackError);
                invokeCallback(callbacks.complete, error, 'complete', options.onCallbackError);
            });
            return undefined;
        },
    });
}
//# sourceMappingURL=action-sheet-adapter.js.map